"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import React, { ReactNode, useRef, useState } from "react"
import SwiperCore from "swiper"
import "swiper/css"
import { A11y, Navigation, Pagination, Scrollbar } from "swiper/modules"
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react"
import { iconsMap } from "../../public/icons/iconsMap"
import { useIsDesktop } from "../common/bit/MediaQueries"
import { sizes } from "../theme/sizes"
import { mediaQueries } from "../theme/theme"

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y])

type SwiperOnMobileProps = {
  children: ReactNode
  swiperProps?: SwiperProps
  sidesButtonsDesktop?: boolean
  iconName?: string
  iconNameMobile?: string
}

const SwiperWrapper = styled(Box)`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  .swiper {
    ${mediaQueries.tabletDown} {
      padding: 0;
    }
  }
  .swiper-wrapper {
    align-items: stretch;
  }
`
const StyledSwiperSlide = styled(SwiperSlide)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 auto;
  height: 100%;
`
const ButtonWrapper = styled(Box)`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: ${sizes.baseTriple};
  ${mediaQueries.tabletDown} {
    margin-top: ${sizes.baseTriple};
  }
  .controllers {
    &.right {
      transform: rotate(180deg);
    }
    &:disabled {
      opacity: 0.5;
    }
    ${mediaQueries.tabletDown} {
      padding: 0;
      svg {
        width: 28px;
        height: 28px;
        margin: 0;
      }
    }
  }
`

const SideButton = styled(Button)`
  position: absolute;
  display: flex;
  width: auto;
  border: 1px solid ${sizes.baseTriple};
  justify-content: center;
  margin-bottom: ${sizes.baseTriple};
  z-index: 10;
  &.controllers {
    left: unset;
    right: -60px;
    bottom: 50%;
    padding: 0;
    margin: 0;
    &.right {
      transform: rotate(180deg);
      left: -60px;
      right: unset;
    }
    ${mediaQueries.tabletDown} {
      right: 0;
      &.right {
        left: 0;
      }
    }
    &:disabled {
      opacity: 0.5;
    }
  }
`

const SwiperWithButtons = ({
  children,
  swiperProps = {},
  sidesButtonsDesktop = false,
  iconName = "arrow_lightOrange_black_line",
  iconNameMobile = "arrow_lightPink_black_line",
}: SwiperOnMobileProps) => {
  const { spaceBetween, slidesPerView, centeredSlides } = swiperProps
  const ArrowRightIcon = iconsMap[iconName]
  const ArrowRightIconMobile = iconNameMobile
    ? iconsMap[iconNameMobile]
    : ArrowRightIcon
  const isDesktop = useIsDesktop()
  const showSidesButtons = sidesButtonsDesktop && isDesktop

  // Create a ref to store the Swiper instance
  const swiperRef = useRef<SwiperCore | null>(null)

  // Track swiper navigation state
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  // Handler functions for navigation
  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
    }
  }

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
    }
  }

  return (
    <SwiperWrapper>
      {children && Array.isArray(children) && children.length > 1 && (
        <>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper
              setIsBeginning(swiper.isBeginning)
              setIsEnd(swiper.isEnd)
            }}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning)
              setIsEnd(swiper.isEnd)
            }}
            spaceBetween={spaceBetween || 20}
            modules={[Navigation]}
            autoHeight={false}
            pagination={false}
            autoplay={false}
            centeredSlides={centeredSlides || true}
            breakpoints={{
              0: {
                slidesPerView: 1,
                slidesPerGroup: 1,
              },
              920: {
                slidesPerView: slidesPerView || 1.75,
              },
            }}
            {...swiperProps}
          >
            {children.map((child, index) => {
              if (React.isValidElement(child)) {
                return (
                  <StyledSwiperSlide
                    key={`swiper-mobile-${index.toString()}-${child.key}`}
                  >
                    {child}
                  </StyledSwiperSlide>
                )
              }
            })}
          </Swiper>
          {(!sidesButtonsDesktop || !isDesktop) && (
            <ButtonWrapper>
              <Button
                className="right controllers"
                variant="ghost"
                onClick={handlePrev}
                disabled={isBeginning}
              >
                {isDesktop ? <ArrowRightIcon /> : <ArrowRightIconMobile />}
              </Button>
              <Button
                className="left controllers"
                variant="ghost"
                onClick={handleNext}
                disabled={isEnd}
              >
                {isDesktop ? <ArrowRightIcon /> : <ArrowRightIconMobile />}
              </Button>
            </ButtonWrapper>
          )}
          <SideButton
            className="right controllers"
            variant="ghost"
            onClick={handlePrev}
            disabled={isBeginning}
          >
            {showSidesButtons && <ArrowRightIcon />}
          </SideButton>
          <SideButton
            className="left controllers"
            variant="ghost"
            onClick={handleNext}
            disabled={isEnd}
          >
            {showSidesButtons && <ArrowRightIcon />}
          </SideButton>
        </>
      )}
    </SwiperWrapper>
  )
}

export default SwiperWithButtons
